import React, { lazy, useContext, useEffect, useState } from 'react';

import { useFeatureIsOn, useGrowthBook } from '@growthbook/growthbook-react';
import { Box } from '@mui/material';
import { queryOptions } from '@tanstack/react-query';
import { Buffer } from 'buffer';
import dayjs from 'dayjs';
import _ from 'lodash';
import mixpanel from 'mixpanel-browser';
import {
  json,
  redirect as routerRedirect,
  useActionData,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import Debug from '../../components/Debug';
import FormActionBar from '../../components/shared/Layout/FormActionBar';
import Loading from '../../components/shared/Loading';
import { INITIAL_CUSTOMER_INFO } from '../../constants/booking';
import { SERVICE_ID } from '../../constants/services';
import { AnalyticsContext } from '../../context/Analytics';
import { CustomerRequestContext } from '../../context/CustomerRequest';
import GoogleMaps from '../../modules/GoogleMaps';
import isQuoteGuaranteed from '../../utils/isQuoteGuaranteed';
import isQuoteLongDistance from '../../utils/isQuoteLongDistance';
import onlyUnique from '../../utils/onlyUnique';
import quoteHasAddon from '../../utils/quoteHasAddon';
import quoteHasTransit from '../../utils/quoteHasTransit';
import useQuote from '../../utils/useQuote';
import { FEES } from '../../constants/fees';

export const VALID_STEPS = {
  SERVICE: 'service',
  LOCATION: 'location',
  LABOR_ONLY_NOTICE: 'labor_only_notice',
  PROPERTY: 'property',
  INVENTORY: 'inventory',
  SPECIAL_INVENTORY: 'special_inventory',
  DATE_TIME: 'date_time',
  DATE: 'date',
  TIME: 'time',
  LEAD_CONFIRMATION: 'lead_confirmation',
  PACKAGE: 'package',
  HOURLY: 'hourly',
  FLEX: 'flex',
  ADDONS: 'addons',
  CHECKOUT: 'checkout',
  CONFIRMATION: 'confirmation',
  VIEW_QUOTE: 'view_quote',
};

const STEPS_REQUIRE_QUOTE = [
  VALID_STEPS.DATE_TIME,
  VALID_STEPS.PACKAGE,
  VALID_STEPS.HOURLY,
  VALID_STEPS.FLEX,
  VALID_STEPS.ADDONS,
  VALID_STEPS.CHECKOUT,
  VALID_STEPS.VIEW_QUOTE,
];

const STEPS_ALLOW_SESSION_REPOPULATION = [VALID_STEPS.VIEW_QUOTE];

const getQuoteById = (id, step) =>
  fetch(
    `${process.env.REACT_APP_WEBPROXY_URI}/v1/public/booking/quote/${id}?aggregate_availability=true&aggregate_available_addons=true&aggregate_customer=true`,
  ).then((response) => {
    if (response.status >= 400) {
      throw json(
        {
          errorLoc: 'loader',
          route: 'step',
          step,
          extra: {
            apiRequestId: response.headers.get('X-Request-Id') ?? 'unknown',
            quoteId: id,
            status: response.status,
            statusText: response.statusText,
          },
        },
        {
          status: response.status,
          statusText: response.statusText,
        },
      );
    }
    return response.json();
  });

export const invalidateQuoteQuery = (queryClient, quoteId) =>
  queryClient.invalidateQueries({ queryKey: ['quote', quoteId] });

export const getQuote = (id, step) => {
  if (!id) {
    return queryOptions({
      enabled: false,
      queryKey: ['no-quote'],
      queryFn: () => ({}),
    });
  }
  return queryOptions({
    enabled: !!id,
    staleTime: 1000 * 10,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    queryKey: ['quote', id],
    queryFn: async () => {
      const quote = await getQuoteById(id, step);
      if (!quote) {
        throw new Response('', {
          status: 404,
          statusText: 'Not found',
        });
      }
      return quote;
    },
  });
};

// Named export `loader` and `action` must return an async function for react router to use with the lazy option
// This wrapping function can be useful to pass special/custom params from the router (such as a query client from react-query)
export const loader =
  (queryClient): any =>
  async ({ request, params }) => {
    const { searchParams } = new URL(request.url);
    const { step } = params;
    const quoteId = searchParams.get('quoteId');

    if (!Object.values(VALID_STEPS).includes(step)) {
      throw json(null, { status: 404, statusText: 'Not Found' });
    }

    if (!STEPS_REQUIRE_QUOTE.includes(step)) {
      // no quote required - exit loader
      return {};
    }

    // quote required - ensure quoteId exists
    if (STEPS_REQUIRE_QUOTE.includes(step) && !quoteId) {
      throw json(
        {
          errorLoc: 'loader',
          route: 'step',
          step,
          extra: {
            message: 'Missing quote id',
          },
        },
        { status: 400, statusText: 'Bad Request' },
      );
    }

    const quoteData = await queryClient.ensureQueryData(getQuote(quoteId, step));

    const currentSession = JSON.parse(window.sessionStorage.getItem('customerInfo'));
    const initialSession = _.cloneDeep(INITIAL_CUSTOMER_INFO);
    delete currentSession.gbIdentity;
    delete initialSession.gbIdentity;

    const sessionDataMatchesInitial = JSON.stringify(currentSession) === JSON.stringify(initialSession);

    if (STEPS_ALLOW_SESSION_REPOPULATION.includes(step) && sessionDataMatchesInitial) {
      // Missing session, re-establish
      // Only allow on defined steps
      if (mixpanel?.track) {
        mixpanel.track('Booking Flow - Step route - no session data in loader - Repopulating', { quoteId, step });
      }
      const { customer } = quoteData;

      const locations = quoteData.service_groups
        .map((sg) => sg.locations.map(({ location, location_id: locationId }) => ({ location, location_id: locationId })))
        .flat()
        .filter(onlyUnique)
        .map(({ location }) => ({
          line_1: location.line_1,
          line_2: location.line_2,
          city: location.city,
          state: location.state,
          country: location.country,
          marketCode: location.market_id,
          postal_code: location.postal_id,
          property_type: location.property_type,
          timezone: location.timezone,
        }));

      const hasTransit = quoteHasTransit(quoteData);

      const startDateTime = quoteData.service_groups[0].start_datetime;
      const startTimezone = quoteData.service_groups[0].start_timezone;

      const dateObject = dayjs(startDateTime).tz(startTimezone);

      const customerRequestData = {
        customer,
        truck: hasTransit,
        locations,
        date: dateObject.format('YYYY-MM-DD'),
        time: dateObject.hour(),
        affiliateAccountId: quoteData.affiliate_id,
      };

      window.sessionStorage.setItem('customerInfo', JSON.stringify(customerRequestData));
    } else if (sessionDataMatchesInitial) {
      if (mixpanel?.track) {
        mixpanel.track('Booking Flow - Step route - no session data in loader - not allowed to repopulate', {
          quoteId,
          step,
        });
      }
    }

    return { quoteId };
  };

export const action =
  (queryClient) =>
  async ({ request, params }) => {
    const { searchParams } = new URL(request.url);
    const quoteId = searchParams.get('quoteId');
    const { step } = params;

    const formData = await request.formData();
    const intent = formData.get('intent')?.toString();
    const paymentMethodToken = formData.get('paymentMethodToken')?.toString();
    const customerSessionString = formData.get('customerSessionData')?.toString();
    const customerSession = customerSessionString ? JSON.parse(customerSessionString) : {};

    let orderResult;

    // Checkout action
    if (step === 'checkout') {
      if (intent === 'apply-promo') {
        const promoCodeResult = await fetch(`${process.env.REACT_APP_WEBPROXY_URI}/v1/public/booking/quote/${quoteId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-User-Id': formData.get('userId').toString(),
          },
          body: JSON.stringify([
            {
              op: 'lookup_replace',
              path: '/promo_code',
              value: {
                promo_code_name: formData.get('promoCode').toString().trim(),
              },
            },
          ]),
        }).then((response) => response.json());
        if (promoCodeResult.errors) {
          const error = promoCodeResult.errors[0];
          if (error) {
            return Promise.resolve({
              ok: true,
              error,
              intent,
            });
          }
        }
        invalidateQuoteQuery(queryClient, quoteId);
        return Promise.resolve({
          ok: true,
          intent,
          data: {
            promoCode: formData.get('promoCode').toString(),
          },
        });
      }
      if (intent === 'remove-promo') {
        const promoCodeResult = await fetch(`${process.env.REACT_APP_WEBPROXY_URI}/v1/public/booking/quote/${quoteId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-User-Id': formData.get('userId').toString(),
          },
          body: JSON.stringify([
            {
              op: 'remove',
              path: `/promo_code/${formData.get('promoCodeId')?.toString()}`,
            },
          ]),
        }).then((response) => response.json());
        if (promoCodeResult.errors) {
          const error = promoCodeResult.errors[0];
          if (error) {
            return Promise.resolve({
              ok: false,
              intent,
              error,
            });
          }
        }
        invalidateQuoteQuery(queryClient, quoteId);
        return Promise.resolve({
          intent,
          result: {
            ok: true,
          },
        });
      }
      if (intent === 'checkout') {
        const customerFirstName = formData.get('firstName').toString();
        const customerLastName = formData.get('lastName').toString();
        const customerPhone = formData.get('phone').toString();
        const customerEmail = formData.get('email').toString();

        orderResult = await fetch(`${process.env.REACT_APP_WEBPROXY_URI}/v1/public/booking/order`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-User-Id': formData.get('userId').toString(),
          },
          body: JSON.stringify({
            quote_id: quoteId,
            payment_method_token: paymentMethodToken,
            stripe_gateway: formData.get('stripeGateway')?.toString() ?? '',
            customer: {
              firstName: customerFirstName,
              lastName: customerLastName,
              phone: customerPhone,
              email: customerEmail,
            },
          }),
        })
          .then((response) => {
            if (response.ok) {
              return response.json();
            }
            const apiRequestId = response.headers.get('X-Request-Id');
            return response.json().then((result) => ({ error: result.message, data: { apiRequestId } }));
          })
          .then(({ id, confirmation_id: confirmationId, long_distance: longDistance, zip, error, data }) => {
            if (error) {
              return Promise.resolve({
                ok: false,
                error,
                intent,
                data: data ?? {},
              });
            }

            return Promise.resolve({
              intent,
              ok: true,
              error: null,
              data: {
                id,
                longDistance,
                confirmationId,
                zip,
              },
            });
          });

        if (!orderResult.ok) {
          if (orderResult.error === 'stripe_gateway_mismatch') {
            // Checkout moved the quote to a customer on a different Stripe account
            // Refetch so Layout reloads Stripe with that customer's publishable key before card is re-entered
            await invalidateQuoteQuery(queryClient, quoteId);
          }
          return orderResult;
        }
      }
    }

    if (step === 'addons') {
      if (intent === 'add-product') {
        const updateResult = await fetch(`${process.env.REACT_APP_WEBPROXY_URI}/v1/public/booking/quote/${quoteId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-User-Id': formData.get('userId').toString(),
          },
          body: JSON.stringify([
            {
              op: 'add',
              path: '/products',
              value: {
                product_id: formData.get('productId').toString(),
                estimated_quantity: 1,
                estimated_unit_price: 0,
              },
            },
          ]),
        }).then((response) => response.json());
        if (updateResult.errors) {
          const error = updateResult.errors[0];
          if (error) {
            return Promise.resolve({
              ok: false,
              intent,
              error,
            });
          }
        }
        invalidateQuoteQuery(queryClient, quoteId);
        return Promise.resolve({
          ok: true,
          intent,
        });
      }
      if (intent === 'remove-product') {
        const updateResult = await fetch(`${process.env.REACT_APP_WEBPROXY_URI}/v1/public/booking/quote/${quoteId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-User-Id': formData.get('userId').toString(),
          },
          body: JSON.stringify([
            {
              op: 'remove',
              path: `/products/${formData.get('quoteProductId').toString()}`,
            },
          ]),
        }).then((response) => response.json());
        if (updateResult.errors) {
          const error = updateResult.errors[0];
          if (error) {
            return Promise.resolve({
              ok: false,
              intent,
              error,
            });
          }
        }
        invalidateQuoteQuery(queryClient, quoteId);
        return Promise.resolve({
          ok: true,
          intent,
        });
      }
      if (intent === 'add-service-group') {
        const selectedDateTime = formData.get('selectedDateTime').toString();
        const serviceId = formData.get('productId').toString();
        const serviceDuration = Number(formData.get('service-addon-duration').toString());
        const serviceWorkers = Number(formData.get('service-addon-workers').toString());

        const updateResult = await fetch(`${process.env.REACT_APP_WEBPROXY_URI}/v1/public/booking/quote/${quoteId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-User-Id': formData.get('userId').toString(),
          },
          body: JSON.stringify([
            {
              op: 'add',
              // Special service function pathing - null subresource ID unused in proxy API
              path: '/service_groups/null/service_details',
              value: {
                service_codes: [serviceId],
                locations: [formData.get('packingServiceLocation').toString()],
                start_datetime: dayjs(selectedDateTime).toISOString(),
                service_details: {
                  duration: serviceDuration,
                  service_workers: {
                    [serviceId]: serviceWorkers,
                  },
                },
              },
            },
          ]),
        }).then((response) => response.json());
        if (updateResult.errors) {
          const error = updateResult.errors[0];
          if (error) {
            return Promise.resolve({
              ok: false,
              intent,
              error,
            });
          }
        }
        invalidateQuoteQuery(queryClient, quoteId);
        return Promise.resolve({
          ok: true,
          intent,
        });
      }
      if (intent === 'remove-service-group') {
        const updateResult = await fetch(`${process.env.REACT_APP_WEBPROXY_URI}/v1/public/booking/quote/${quoteId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-User-Id': formData.get('userId').toString(),
          },
          body: JSON.stringify([
            {
              op: 'remove',
              path: `/service_groups/${formData.get('removeQuoteServiceGroupId').toString()}`,
            },
          ]),
        }).then((response) => response.json());
        if (updateResult.errors) {
          const error = updateResult.errors[0];
          if (error) {
            return Promise.resolve({
              ok: false,
              intent,
              error,
            });
          }
        }
        invalidateQuoteQuery(queryClient, quoteId);
        return Promise.resolve({
          ok: true,
          intent,
        });
      }
      if (intent !== 'next-step') {
        return Promise.resolve({
          ok: false,
          intent,
          error: 'Unknown Intent',
        });
      }
    }

    if (step === 'flex') {
      if (intent === 'apply-flex') {
        const updateResult = await fetch(`${process.env.REACT_APP_WEBPROXY_URI}/v1/public/booking/quote/${quoteId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-User-Id': formData.get('userId').toString(),
          },
          body: JSON.stringify([
            {
              op: 'replace',
              path: `/service_groups/${formData.get('flexServiceGroupId')}/flex_quote`,
              value: formData.get('selectedFlexQuoteId'),
            },
          ]),
        }).then((response) => response.json());
        if (updateResult.errors) {
          const error = updateResult.errors[0];
          if (error) {
            return Promise.resolve({
              ok: false,
              intent,
              error,
            });
          }
        }
        invalidateQuoteQuery(queryClient, quoteId);
      }
    }

    if (step === 'package' || step === 'hourly') {
      if (intent === 'modify-service-details') {
        const updateResult = await fetch(`${process.env.REACT_APP_WEBPROXY_URI}/v1/public/booking/quote/${quoteId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-User-Id': formData.get('userId').toString(),
          },
          body: JSON.stringify([
            {
              op: 'replace',
              path: `/service_groups/${formData.get('serviceGroupId').toString()}/service_details`,
              value: {
                duration: formData.get('newHours').toString(),
                service_workers: JSON.parse(formData.get('newWorkers')?.toString()),
              },
            },
          ]),
        }).then((response) => response.json());
        if (updateResult.errors) {
          const error = updateResult.errors[0];
          if (error) {
            return Promise.resolve({
              ok: false,
              intent,
              error,
            });
          }
        }
        invalidateQuoteQuery(queryClient, quoteId);
        return Promise.resolve({
          ok: true,
          intent,
        });
      }
      if (intent === 'next-step') {
        const formInventory = formData.get('inventory');
        const formNotes = formData.get('notes');

        if (formInventory && formNotes) {
          const quoteInventory = JSON.parse(formInventory.toString());
          const updateResult = await fetch(`${process.env.REACT_APP_WEBPROXY_URI}/v1/public/booking/quote/${quoteId}`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'X-User-Id': formData.get('userId').toString(),
            },
            body: JSON.stringify([
              {
                op: 'replace',
                path: '/inventory',
                value: {
                  ...quoteInventory,
                  notes: formNotes.toString(),
                },
              },
            ]),
          }).then((response) => response.json());
          if (updateResult.errors) {
            const error = updateResult.errors[0];
            if (error) {
              return Promise.resolve({
                ok: false,
                intent,
                error,
              });
            }
          }
        }
      }
    }

    if (step === 'date_time') {
      try {
        const loadingServiceGroupId = formData.get('loadingServiceGroupId');
        const unloadingServiceGroupId = formData.get('unloadingServiceGroupId');
        const loadingFlexQuoteId = formData.get('loadingFlexQuoteId');
        const unloadingFlexQuoteId = formData.get('unloadingFlexQuoteId');
        const transitServiceGroupId = formData.get('transitServiceGroupId');
        const loadingFlexStartDateTime = formData.get('loadingFlexStartDateTime');
        const unloadingFlexStartDateTime = formData.get('unloadingFlexStartDateTime');
        const transitTrucks = formData.get('transitTrucks');
        const transitServiceId = formData.get('transitServiceId');

        const updateResult = await fetch(`${process.env.REACT_APP_WEBPROXY_URI}/v1/public/booking/quote/${quoteId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-User-Id': formData.get('userId').toString(),
          },
          // TODO: Move LD flex logic to proxy? can avoid a lot of the form data by loading quote on proxy and setting things appropriately
          body: JSON.stringify([
            {
              op: 'replace',
              path: `/service_groups/${loadingServiceGroupId}/flex_quote`,
              value: loadingFlexQuoteId,
            },
            {
              op: 'replace',
              path: `/service_groups/${unloadingServiceGroupId}/flex_quote`,
              value: unloadingFlexQuoteId,
            },
            {
              op: 'replace',
              path: `/service_groups/${transitServiceGroupId}/start_time`,
              value: {
                start_datetime: dayjs(loadingFlexStartDateTime).toISOString(),
              },
            },
            {
              op: 'replace',
              path: `/service_groups/${transitServiceGroupId}/service_details`,
              value: {
                duration: dayjs(unloadingFlexStartDateTime).diff(dayjs(loadingFlexStartDateTime), 'hours'),
                service_workers: {
                  [transitServiceId ?? 'TRANSIT']: transitTrucks,
                },
              },
            },
          ]),
        }).then((response) => response.json());
        if (updateResult.errors) {
          const error = updateResult.errors[0];
          if (error) {
            return Promise.resolve({
              ok: false,
              intent,
              error,
            });
          }
        }
        invalidateQuoteQuery(queryClient, quoteId);
      } catch (error: any) {
        return Promise.resolve({
          ok: false,
          error,
        });
      }
    }

    // Next step - flow control - always last
    const stepResponse = await fetch(`${process.env.REACT_APP_WEBPROXY_URI}/v1/public/booking/quote/step`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-User-Id': formData.get('userId').toString(),
      },
      body: JSON.stringify({
        quote_id: quoteId,
        current_step: step,
        customer_request: customerSession,
      }),
    });

    const stepData: any = await stepResponse.json();

    if (stepData.next_step) {
      if (stepData.quote_data) {
        if (!quoteId) {
          // No search param quote id exists and next step returned quote data, append to search params for next step
          searchParams.set('quoteId', stepData.quote_data.id);
        }
        // step returned data - set query data - loader will use this data on next step wihtout having to make call itself
        queryClient.setQueryData(['quote', quoteId ?? stepData.quote_data.id], stepData.quote_data);
      } else {
        // step returned no data - invalidate query so loader can make call - in theory step should always return data - safety fallback
        invalidateQuoteQuery(queryClient, quoteId);
      }

      // Strip prepopulated truck when continuing
      if (searchParams.get('truck')) {
        searchParams.delete('truck');
      }

      let searchParamsString = searchParams.toString() ?? '';

      if (stepData.next_step === 'entry') {
        const entryInfo = {
          ...customerSession,
          locations: customerSession?.locations?.map(({ propertyInfo, ...rest }) => ({ address: rest, propertyInfo })) ?? [],
        };

        if (stepData.metadata?.mlcAvailable) {
          entryInfo.mlcAvailable = true;
        }

        if (stepData.metadata?.entryLead) {
          entryInfo.lead = true;
          entryInfo.leadId = null;
        }

        const encodedInfo = Buffer.from(JSON.stringify(entryInfo)).toString('base64');
        searchParamsString = `info=${encodedInfo}${searchParamsString ? `&${searchParamsString}` : ''}`;

        const nextStepUrl = `/${stepData.next_step}${searchParamsString ? `?${searchParamsString}` : ''}`;
        return routerRedirect(nextStepUrl);
      }

      const redirectData: any = {};

      if (stepData.metadata?.startingLocationTimezone) {
        redirectData.startingLocationTimezone = stepData.metadata?.startingLocationTimezone;
      }

      if (stepData.metadata?.marketRouteIndicators?.routeData?.driving_distance) {
        redirectData.mileage = stepData.metadata?.marketRouteIndicators?.routeData?.driving_distance;
      }

      if (stepData.metadata?.marketRouteIndicators?.isLD && !stepData.metadata?.marketRouteIndicators?.isLDHireOtherMovers) {
        redirectData.multiLDLaborOnly = true;
      }

      if (stepData.metadata?.leadData?.lead_id) {
        const nextStepUrl = `/${stepData.next_step}${searchParamsString ? `?${searchParamsString}` : ''}`;

        return Promise.resolve({
          redirect: nextStepUrl,
          intent,
          ok: true,
          error: null,
          data: {
            ...redirectData,
            leadId: stepData.metadata.leadData.lead_id,
            leadRecordType: stepData.metadata.leadData.lead_record_type,
            leadSource: stepData.metadata.leadData.lead_source,
            leadType: stepData.metadata.leadData.lead_type,
          },
        });
      }

      if (orderResult) {
        if (!searchParams.get('orderId')) {
          searchParams.append('orderId', orderResult.data.id);
        }

        searchParamsString = searchParams.toString() ?? '';
        const nextStepUrl = `/${stepData.next_step}${searchParamsString ? `?${searchParamsString}` : ''}`;
        return Promise.resolve({
          redirect: nextStepUrl,
          intent,
          ok: true,
          error: null,
          data: {
            ...(orderResult.data ?? {}),
          },
        });
      }

      const nextStepUrl = `/${stepData.next_step}/${searchParamsString ? `?${searchParamsString}` : ''}`;
      return routerRedirect(nextStepUrl);
    }

    // const headerKeys = stepResponse.headers.keys().map(key => key);
    // console.log(headerKeys);

    return {
      ok: false,
      error: {
        message: 'no next step returned',
        detail: (stepData?.message || stepData?.detail) ?? 'Unknown reason',
        requestId: stepResponse?.headers?.get('X-Request-Id'),
      },
    };
  };

const ServiceComponent = lazy(() => import('../../components/Step/Service'));
const LocationComponent = lazy(() => import('../../components/Step/Location'));
const LaborOnlyNoticeComponent = lazy(() => import('../../components/Step/LaborOnlyNotice'));
const PropertyComponent = lazy(() => import('../../components/Step/Property'));
const InventoryComponent = lazy(() => import('../../components/Step/Inventory'));
const SpecialInventoryComponent = lazy(() => import('../../components/Step/SpecialInventory'));
const DateTimeComponent = lazy(() => import('../../components/Step/DateTime'));
const DateComponent = lazy(() => import('../../components/Step/Date'));
const TimeComponent = lazy(() => import('../../components/Step/Time'));
const LeadConfirmationComponent = lazy(() => import('../../components/Step/LeadConfirmation'));
const QuoteComponent = lazy(() => import('../../components/Step/Package'));
const LDQuoteComponent = lazy(() => import('../../components/Step/LDQuote'));
const FlexComponent = lazy(() => import('../../components/Step/Flex'));
const CheckoutComponent = lazy(() => import('../../components/Step/Checkout'));
const AddonComponent = lazy(() => import('../../components/Step/Addons'));
const ConfirmationComponent = lazy(() => import('../../components/Step/Confirmation'));
const ViewQuoteComponent = lazy(() => import('../../components/Step/ViewQuote'));

// These steps need action bar with continue button
const ACTION_BAR_STEPS = [
  VALID_STEPS.SERVICE,
  VALID_STEPS.LOCATION,
  VALID_STEPS.LABOR_ONLY_NOTICE,
  VALID_STEPS.PROPERTY,
  VALID_STEPS.INVENTORY,
  VALID_STEPS.SPECIAL_INVENTORY,
  VALID_STEPS.DATE_TIME,
  VALID_STEPS.DATE,
  VALID_STEPS.TIME,
  VALID_STEPS.ADDONS,
  VALID_STEPS.CHECKOUT,
  VALID_STEPS.FLEX,
  VALID_STEPS.HOURLY,
  VALID_STEPS.PACKAGE,
];

export const Component = () => {
  // A/A Test for data sanity - does not impact view in anyway
  useFeatureIsOn('aa-test');

  const { step } = useParams();
  const { data: quoteData } = useQuote();

  const { track, ready: analyticsReady, userId: analyticsUserId } = useContext(AnalyticsContext);
  const { customerInfo, setCustomerInfo } = useContext(CustomerRequestContext);

  const [googleMapsMounted, setGoogleMapsMounted] = useState(false);

  const actionData: any = useActionData();
  const navigate = useNavigate();
  const growthbook = useGrowthBook();
  const { state: locationState } = useLocation();
  const { entry, leadId, leadType, quoteId } = locationState || {};

  const stepNeedsActionBar = ACTION_BAR_STEPS.includes(step);

  const [searchParams] = useSearchParams();

  const promoCodeParam = searchParams.get('pc');

  useEffect(() => {
    if (!analyticsReady) {
      return;
    }
    if (analyticsUserId) {
      mixpanel.identify(analyticsUserId);
    }
    if (growthbook && analyticsUserId) {
      setCustomerInfo({ ...customerInfo, gbIdentity: analyticsUserId });
      growthbook.setAttributes({ id: analyticsUserId });
    } else if (growthbook) {
      // Fallback
      const id = mixpanel.get_distinct_id();
      setCustomerInfo({ ...customerInfo, gbIdentity: id });
      growthbook.setAttributes({ id });
    }
  }, [growthbook, analyticsReady, analyticsUserId]);

  useEffect(() => {
    if (promoCodeParam) {
      setCustomerInfo({ ...customerInfo, promo: promoCodeParam.trim() });
    }
  }, [promoCodeParam]);

  const movingLaborServices = ['HOURLYLABOR', 'LOADING', 'LOADINGUNLOADING', 'UNLOADING'];

  const hasService = (serviceGroup, serviceId) =>
    _.findIndex(serviceGroup?.services, (service: any) => service.service_id === serviceId) >= 0;
  const movingServiceGroup = quoteData?.service_groups?.find((sg) =>
    movingLaborServices.some((service) => hasService(sg, service)),
  );

  const loadingServiceGroup = quoteData?.service_groups?.find(
    (sg) => hasService(sg, SERVICE_ID.LOADING) || hasService(sg, SERVICE_ID.LDLOADING),
  );

  const unloadingServiceGroup = quoteData?.service_groups?.find(
    (sg) => hasService(sg, SERVICE_ID.UNLOADING) || hasService(sg, SERVICE_ID.LDUNLOADING),
  );

  const transitServiceGroup = quoteData?.service_groups?.find(
    (sg) => hasService(sg, SERVICE_ID.TRANSIT) || hasService(sg, SERVICE_ID.LDTRANSIT),
  );

  const packingServiceGroup = quoteData?.service_groups?.find((sg) => hasService(sg, SERVICE_ID.PACKINGSERVICE));

  const isLongDistance = isQuoteLongDistance(quoteData ?? {});

  useEffect(() => {
    if (!analyticsReady) return;

    const { locations, date } = customerInfo;

    track('Booking - Page Viewed', {
      page: step,
      ...(_.isEmpty(quoteData)
        ? {}
        : {
            quoteId: quoteData.id,
            quoteGuaranteed: isQuoteGuaranteed(quoteData),
            quoteLongDistance: isQuoteLongDistance(quoteData),
            quoteAffiliate: quoteData.affiliate_id,
          }),
      customerInfo,
      ...(typeof window !== 'undefined'
        ? { fullUrlLength: window?.location?.href?.length, queryParamLength: window?.location?.search?.length }
        : {}),
    });
    if (step === 'package' || step === 'hourly') {
      const services = quoteData?.service_groups?.reduce((prev, serviceGroup) => {
        const tempServices = serviceGroup.services.map((service) => ({
          id: service.service_id,
          price: service.quoted_unit_price,
          pricingStrategy: service.estimated_pricing_strategy,
          totalPrice: service.total_cost,
          workers: service.quoted_workers,
          duration: service.quoted_hours,
          available: service.available,
        }));
        prev.push(...tempServices);
        return prev;
      }, []);

      const fees = quoteData?.price_adjustments?.filter(({ adjustment_family: adjFamily }) => adjFamily === 'Fee');
      const parsedFees = fees?.map((fee) => ({
        id: fee.adjustment_id,
        name: fee.name,
        type: fee.adjustment_type,
        value: fee.adjustment_value,
        amount: fee.estimated_amount,
      }));

      const servicesTotal = services.reduce((prev, service) => prev + service.totalPrice, 0);
      const feesTotal = parsedFees.reduce((prev, fee) => prev + fee.amount, 0);
      const total = servicesTotal + feesTotal;

      const isLDQuote = isQuoteLongDistance(quoteData);
      const hasPacking = packingServiceGroup && quoteHasAddon(quoteData, SERVICE_ID.PACKINGSERVICE);

      const ldQuoteData = isLDQuote
        ? {
            loadingLaborWorkers: loadingServiceGroup?.services?.[0]?.quoted_workers,
            loadingLaborDuration: loadingServiceGroup?.services?.[0]?.quoted_hours,
            unloadingLaborWorkers: unloadingServiceGroup?.services?.[0]?.quoted_workers,
            unloadingLaborDuration: unloadingServiceGroup?.services?.[0]?.quoted_hours,
            trucks: transitServiceGroup?.services?.[0]?.quoted_workers,
            loadingTotal: loadingServiceGroup?.total_cost,
            unloadingTotal: unloadingServiceGroup?.total_cost,
            transitTotal: transitServiceGroup?.total_cost,
            mileage: customerInfo?.metadata?.mileage,
            ...(hasPacking
              ? {
                  packingWorkers: packingServiceGroup?.services?.[0]?.quoted_workers,
                  packingDuration: packingServiceGroup?.services?.[0]?.quoted_hours,
                  packingTotal: packingServiceGroup?.total_cost,
                }
              : {}),
          }
        : null;

      const detailsData = {
        services,
        area: quoteData?.inventory?.area,
        targetDate: date,
        locations,
        fees: parsedFees,
        servicesTotal,
        feesTotal,
        total,
        available: quoteData?.is_available,
        guaranteed: isQuoteGuaranteed(quoteData),
        longDistance: isLDQuote,
        packingService: hasPacking,
        ldQuoteData,
        hasExtendedMileageFee: parsedFees.some((fee) => fee.id === FEES.EXTENDED_MILEAGE_FEE),
      };
      track('Booking - Quote Details', detailsData);
      // Need to filter down quote data to not exceed mixpanel property limits
      const filteredQuoteData = { ...quoteData };
      delete filteredQuoteData.inventory;
      delete filteredQuoteData.price_adjustments;
      track('Booking - Quote Availability', {
        quoteData: filteredQuoteData,
      });
    }
  }, [analyticsReady, step]);

  useEffect(() => {
    (async () => {
      if (actionData) {
        const { intent, ok, data, redirect } = actionData;
        if (ok) {
          if (intent === 'checkout') {
            const services = quoteData?.service_groups?.reduce((prev, serviceGroup) => {
              const tempServices = serviceGroup?.services?.map(({ service_id: serviceId }) => serviceId) ?? [];
              prev.push(...tempServices);
              return prev;
            }, []);

            const eventPayload = {
              order_id: data.confirmationId,
              revenue: quoteData.totals.subtotal / 100,
              discount: quoteData.totals.discount_total / 100,
              coupon: quoteData.price_adjustments?.find((adj) => adj.adjustment_id === 'PROMOCODE')?.name,
              deposit: quoteData.totals.deposit_amount / 100,
              currency: 'USD',
              firstName: customerInfo.customer.firstName,
              lastName: customerInfo.customer.lastName,
              email: customerInfo.customer.email,
              phone: customerInfo.customer.phone,
              guaranteed: isQuoteGuaranteed(quoteData),
              longDistance: isQuoteLongDistance(quoteData),
              services,
              affiliate: quoteData.affiliate_id,
              properties: [
                {
                  sku: quoteData.id,
                  category: 'Hourly',
                },
              ],
              context: {
                traits: {
                  firstName: customerInfo.customer.firstName,
                  lastName: customerInfo.customer.lastName,
                  email: customerInfo.customer.email,
                  phone: customerInfo.customer.phone,
                  address: {
                    ...(movingServiceGroup
                      ? {
                          city: movingServiceGroup.locations[0].location.city,
                          state: movingServiceGroup.locations[0].location.state,
                          postalCode: movingServiceGroup.locations[0].location.postal_id,
                        }
                      : {}),
                    ...(loadingServiceGroup
                      ? {
                          city: loadingServiceGroup.locations[0].location.city,
                          state: loadingServiceGroup.locations[0].location.state,
                          postalCode: loadingServiceGroup.locations[0].location.postal_id,
                        }
                      : {}),
                  },
                },
              },
            };
            await track('Order Completed', eventPayload);
            if (redirect.startsWith('http')) {
              // External Redirect
              window.location.href = redirect;
            } else {
              // Only internal redirect use case is LD, local goes straight to Dashboard using above external redirect
              const redirectState = {
                orderId: data.id,
                confirmationId: data.confirmationId,
                longDistance: data.longDistance,
                zip: data.zip,
              };
              // Internal Redirect
              navigate(redirect, {
                state: redirectState,
              });
            }
            return;
          }
        }
        if (redirect) {
          if (redirect.startsWith('http')) {
            // External Redirect
            window.location.href = redirect;
          } else if (data.leadId) {
            // Internal Redirect with lead data
            navigate(redirect, { state: { ...data } });
          } else {
            navigate(redirect);
          }
        }
      }
    })();
  }, [actionData]);

  const stepComponents = {
    [VALID_STEPS.SERVICE]: <ServiceComponent />,
    [VALID_STEPS.LOCATION]: <LocationComponent googleMapsMounted={googleMapsMounted} />,
    [VALID_STEPS.LABOR_ONLY_NOTICE]: <LaborOnlyNoticeComponent />,
    [VALID_STEPS.PROPERTY]: <PropertyComponent />,
    [VALID_STEPS.INVENTORY]: <InventoryComponent />,
    [VALID_STEPS.SPECIAL_INVENTORY]: <SpecialInventoryComponent />,
    [VALID_STEPS.DATE_TIME]: <DateTimeComponent />,
    [VALID_STEPS.DATE]: <DateComponent />,
    [VALID_STEPS.TIME]: <TimeComponent />,
    [VALID_STEPS.LEAD_CONFIRMATION]: <LeadConfirmationComponent />,
    [VALID_STEPS.HOURLY]: <QuoteComponent />,
    [VALID_STEPS.PACKAGE]: isLongDistance ? <LDQuoteComponent /> : <QuoteComponent />,
    [VALID_STEPS.ADDONS]: <AddonComponent />,
    [VALID_STEPS.FLEX]: <FlexComponent />,
    [VALID_STEPS.CHECKOUT]: <CheckoutComponent />,
    [VALID_STEPS.ADDONS]: <AddonComponent />,
    [VALID_STEPS.CONFIRMATION]: <ConfirmationComponent />,
    [VALID_STEPS.VIEW_QUOTE]: <ViewQuoteComponent />,
  };

  useEffect(() => {
    if (entry) {
      track('Booking - V2 Entry Successful', {
        ...(leadId ? { leadId, leadType } : {}),
        ...(quoteId ? { quoteId } : {}),
      });
    }
  }, [entry]);

  if (!analyticsReady) {
    return (
      <Box display="flex" justifyContent="center">
        <Loading text="Loading" />
      </Box>
    );
  }

  if (stepComponents[step]) {
    return (
      <>
        {stepComponents[step]}
        {stepNeedsActionBar ? <FormActionBar /> : null}
        {process.env.REACT_APP_DEBUG_ENABLED ? <Debug /> : null}
        <GoogleMaps mounted={googleMapsMounted} setMounted={setGoogleMapsMounted} />
      </>
    );
  }

  return null;
};

Component.displayName = 'Step';
